import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import ShareButton from './ShareButton';       // ← NEW
import '../styles/Message.css';
import '../styles/CortexMessage.css';
import '../styles/CortexStreaming.css';         // ◀── STREAMING: new styles

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ════════════════════════════════════════════════════════════════
// CURRENCY SANITIZER — replaces $number with ₹number
// ════════════════════════════════════════════════════════════════
const fixCurrencyINR = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\$(\d[\d,]*\.?\d*)/g, '₹$1');
};

// ════════════════════════════════════════════════════════════════
// CHART.JS VISUALIZATION (legacy backend — bar/pie/line)
// ════════════════════════════════════════════════════════════════

const ChartVisualization = ({ chartConfig }) => {
  if (!chartConfig || !chartConfig.type) return null;

  const chartOptions = {
    ...chartConfig.options,
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      ...chartConfig.options?.plugins,
      legend: {
        ...chartConfig.options?.plugins?.legend,
        display: true,
        position: chartConfig.type === 'pie' ? 'right' : (chartConfig.options?.plugins?.legend?.position || 'top'),
        labels: {
          ...chartConfig.options?.plugins?.legend?.labels,
          padding: 12,
          usePointStyle: true,
          font: { size: 11 },
          generateLabels: chartConfig.type === 'pie' ? (chart) => {
            const datasets = chart.data.datasets;
            if (datasets.length) {
              return chart.data.labels.map((label, i) => {
                const meta = chart.getDatasetMeta(0);
                const style = meta.controller.getStyle(i);
                return {
                  text: label,
                  fillStyle: style.backgroundColor,
                  strokeStyle: style.borderColor,
                  hidden: !chart.getDataVisibility(i),
                  index: i
                };
              });
            }
            return [];
          } : undefined
        }
      },
      title: {
        ...chartConfig.options?.plugins?.title,
        display: !!chartConfig.options?.plugins?.title?.text,
        font: { size: 13, weight: '600' },
        padding: { bottom: 16 }
      },
      tooltip: {
        ...chartConfig.options?.plugins?.tooltip,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        padding: 10,
        cornerRadius: 6,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || context.label || '';
            if (label) label += ': ';
            const value = context.parsed?.y ?? context.parsed ?? context.raw;
            if (typeof value === 'number') {
              label += value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
            } else {
              label += value;
            }
            return label;
          }
        }
      }
    }
  };

  return (
    <div className="chart-container">
      <div className="chart-wrapper">
        {chartConfig.type === 'bar' && <Bar data={chartConfig.data} options={chartOptions} />}
        {chartConfig.type === 'pie' && <Pie data={chartConfig.data} options={chartOptions} />}
        {chartConfig.type === 'line' && <Line data={chartConfig.data} options={chartOptions} />}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// VEGA-LITE SPEC SANITIZER
// Deep-cleans a Cortex-generated Vega-Lite spec so vega-embed
// doesn't crash on malformed/missing fields.
//
// The "isString" error occurs when vega-embed's internal compiler
// encounters undefined/null where it expects a string (typically in
// encoding channel definitions, $schema, or mark type).
// ════════════════════════════════════════════════════════════════

function sanitizeVegaLiteSpec(rawSpec) {
  if (!rawSpec || typeof rawSpec !== 'object') {
    return null;
  }

  // Deep clone to avoid mutating the original
  let spec;
  try {
    spec = JSON.parse(JSON.stringify(rawSpec));
  } catch {
    return null;
  }

  // 1. Ensure $schema exists — vega-embed's isString() crash often happens here
  if (!spec.$schema) {
    spec.$schema = 'https://vega.github.io/schema/vega-lite/v5.json';
  }

  // 2. Validate mark
  if (spec.mark) {
    if (typeof spec.mark === 'string') {
      // OK
    } else if (typeof spec.mark === 'object' && spec.mark !== null) {
      if (!spec.mark.type || typeof spec.mark.type !== 'string') {
        spec.mark.type = 'bar'; // safe default
      }
    } else {
      spec.mark = 'bar';
    }
  } else if (!spec.layer && !spec.hconcat && !spec.vconcat && !spec.concat) {
    // No mark and not a composite spec — can't render
    return null;
  }

  // 3. Validate data
  if (spec.data) {
    if (spec.data.values !== undefined && !Array.isArray(spec.data.values)) {
      return null;
    }
    if (spec.data.url !== undefined && typeof spec.data.url !== 'string') {
      return null;
    }
    // Empty values array — nothing to chart
    if (Array.isArray(spec.data.values) && spec.data.values.length === 0) {
      return null;
    }
  } else if (!spec.layer) {
    return null;
  }

  // 4. Sanitize encoding — this is where most "isString" crashes originate
  if (spec.encoding && typeof spec.encoding === 'object') {
    const validTypes = ['quantitative', 'temporal', 'ordinal', 'nominal', 'geojson'];

    for (const [channel, enc] of Object.entries(spec.encoding)) {
      // Remove null/undefined encodings
      if (enc === null || enc === undefined) {
        delete spec.encoding[channel];
        continue;
      }

      // Must be an object
      if (typeof enc !== 'object') {
        delete spec.encoding[channel];
        continue;
      }

      // field must be a string
      if (enc.field !== undefined) {
        if (typeof enc.field !== 'string') {
          try {
            enc.field = String(enc.field);
          } catch {
            delete spec.encoding[channel];
            continue;
          }
        }
      }

      // type must be a valid vega-lite type
      if (enc.type !== undefined) {
        if (typeof enc.type !== 'string' || !validTypes.includes(enc.type)) {
          const typeStr = String(enc.type || '').toLowerCase();
          if (validTypes.includes(typeStr)) {
            enc.type = typeStr;
          } else {
            // Remove invalid type — let vega-lite infer
            delete enc.type;
          }
        }
      }

      // title must be a string if present
      if (enc.title !== undefined && typeof enc.title !== 'string') {
        try {
          enc.title = String(enc.title);
        } catch {
          delete enc.title;
        }
      }

      // axis/legend must be objects or null
      for (const prop of ['axis', 'legend', 'scale', 'sort']) {
        if (enc[prop] !== undefined && enc[prop] !== null && typeof enc[prop] !== 'object' && typeof enc[prop] !== 'string') {
          delete enc[prop];
        }
      }

      // aggregate must be a string
      if (enc.aggregate !== undefined && typeof enc.aggregate !== 'string') {
        delete enc.aggregate;
      }
    }
  }

  // 5. Sanitize layer specs recursively
  if (Array.isArray(spec.layer)) {
    spec.layer = spec.layer.map(layerSpec => {
      if (layerSpec && typeof layerSpec === 'object') {
        // Recursively sanitize each layer's encoding
        const sanitized = sanitizeVegaLiteSpec({ ...spec, ...layerSpec, layer: undefined });
        if (sanitized) {
          // Only return the layer-specific parts
          const { $schema, data, ...layerParts } = sanitized;
          return layerParts;
        }
      }
      return layerSpec;
    }).filter(Boolean);

    if (spec.layer.length === 0) {
      return null;
    }
  }

  // 6. Ensure title is a string if present
  if (spec.title !== undefined) {
    if (typeof spec.title === 'object' && spec.title !== null) {
      if (spec.title.text !== undefined && typeof spec.title.text !== 'string') {
        spec.title.text = String(spec.title.text || '');
      }
    } else if (typeof spec.title !== 'string') {
      spec.title = String(spec.title || '');
    }
  }

  return spec;
}

// ════════════════════════════════════════════════════════════════
// VEGA-LITE CHART (Cortex Agent — rendered via Vega-Embed CDN)
// ════════════════════════════════════════════════════════════════

const VegaLiteChart = ({ spec }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!spec || !containerRef.current) return;
    let cancelled = false;

    const renderChart = async () => {
      try {
        setLoading(true);
        setError(null);

        // Sanitize the spec before rendering
        const cleanSpec = sanitizeVegaLiteSpec(spec);
        if (!cleanSpec) {
          console.warn('[VegaLite] Spec rejected by sanitizer:', spec);
          setError('Chart data is incomplete or malformed');
          setLoading(false);
          return;
        }

        const vegaEmbed = await loadVegaEmbed();
        if (cancelled || !containerRef.current) return;

        const enhancedSpec = {
          ...cleanSpec,
          width: 'container',
          height: 300,
          autosize: { type: 'fit', contains: 'padding' },
          config: {
            ...cleanSpec.config,
            background: 'transparent',
            view: { stroke: 'transparent' },
            font: '"Inter", "Segoe UI", system-ui, sans-serif',
            axis: { labelFontSize: 11, titleFontSize: 12, labelColor: '#64748b', titleColor: '#334155', gridColor: '#e2e8f0' },
            legend: { labelFontSize: 11, titleFontSize: 12, labelColor: '#475569' },
            title: { fontSize: 14, fontWeight: 600, color: '#1e293b' }
          }
        };

        await vegaEmbed(containerRef.current, enhancedSpec, {
          actions: false, renderer: 'svg', theme: 'latimes',
        });
        if (!cancelled) setLoading(false);
      } catch (err) {
        // Log the spec that caused the crash for debugging
        console.error('[VegaLite] Render error:', err.message);
        console.error('[VegaLite] Failing spec:', JSON.stringify(spec, null, 2));
        if (!cancelled) {
          setError('Chart could not be rendered');
          setLoading(false);
        }
      }
    };

    renderChart();
    return () => { cancelled = true; };
  }, [spec]);

  if (error) {
    return (
      <div className="cortex-chart-error">
        <span className="cortex-chart-error-icon">⚠️</span>
        <span>Chart rendering failed: {error}</span>
      </div>
    );
  }

  return (
    <div className="cortex-chart-container">
      {loading && (
        <div className="cortex-chart-loading">
          <div className="cortex-chart-spinner" />
          <span>Rendering chart...</span>
        </div>
      )}
      <div ref={containerRef} className="cortex-chart-canvas" />
    </div>
  );
};

let vegaEmbedPromise = null;
function loadVegaEmbed() {
  if (vegaEmbedPromise) return vegaEmbedPromise;
  vegaEmbedPromise = new Promise((resolve, reject) => {
    if (window.vegaEmbed) { resolve(window.vegaEmbed); return; }

    // Pin specific compatible versions to avoid isString/compilation errors.
    // vega-embed@6 (unversioned) can auto-resolve to a build where vega-lite
    // internals mismatch, causing "Cannot read properties of undefined (reading 'isString')".
    // These three versions are tested compatible:
    const scripts = [
      'https://cdn.jsdelivr.net/npm/vega@5.25.0',
      'https://cdn.jsdelivr.net/npm/vega-lite@5.16.3',
      'https://cdn.jsdelivr.net/npm/vega-embed@6.22.2',
    ];

    let loaded = 0;
    const loadNext = () => {
      if (loaded >= scripts.length) {
        if (window.vegaEmbed) {
          resolve(window.vegaEmbed);
        } else {
          vegaEmbedPromise = null;
          reject(new Error('vega-embed not available after loading scripts'));
        }
        return;
      }
      const script = document.createElement('script');
      script.src = scripts[loaded];
      script.onload = () => { loaded++; loadNext(); };
      script.onerror = () => {
        vegaEmbedPromise = null;
        reject(new Error(`Failed to load ${scripts[loaded]}`));
      };
      document.head.appendChild(script);
    };
    loadNext();
  });
  return vegaEmbedPromise;
}

// ════════════════════════════════════════════════════════════════
// EXPANDABLE SECTION
// ════════════════════════════════════════════════════════════════

const ExpandableSection = ({ icon, title, badge, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useRef(null);

  return (
    <div className={`cortex-expandable ${isOpen ? 'cortex-expandable--open' : ''}`}>
      <button className="cortex-expandable__header" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen}>
        <span className="cortex-expandable__icon">{icon}</span>
        <span className="cortex-expandable__title">{title}</span>
        {badge && <span className="cortex-expandable__badge">{badge}</span>}
        <svg className={`cortex-expandable__chevron ${isOpen ? 'cortex-expandable__chevron--open' : ''}`}
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div className="cortex-expandable__body" ref={contentRef}
        style={{ maxHeight: isOpen ? (contentRef.current?.scrollHeight || 2000) + 'px' : '0px' }}>
        <div className="cortex-expandable__content">{children}</div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// MARKDOWN CONTENT (shared)
// ════════════════════════════════════════════════════════════════

const MarkdownContent = ({ content }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]}
    components={{
      table: ({ node, ...props }) => <div className="markdown-table-container"><table className="markdown-table" {...props} /></div>,
      th: ({ node, ...props }) => <th className="markdown-th" {...props} />,
      td: ({ node, ...props }) => <td className="markdown-td" {...props} />,
      h1: ({ node, children, ...props }) => <h1 className="markdown-h1" {...props}>{children}</h1>,
      h2: ({ node, children, ...props }) => <h2 className="markdown-h2" {...props}>{children}</h2>,
      h3: ({ node, children, ...props }) => <h3 className="markdown-h3" {...props}>{children}</h3>,
      ul: ({ node, ...props }) => <ul className="markdown-list" {...props} />,
      ol: ({ node, ...props }) => <ol className="markdown-list" {...props} />,
      code: ({ node, inline, ...props }) =>
        inline ? <code className="markdown-inline-code" {...props} /> : <code className="markdown-code-block" {...props} />
    }}>
    {fixCurrencyINR(content)}
  </ReactMarkdown>
);

// ════════════════════════════════════════════════════════════════
// STREAMING UI COMPONENTS
// ════════════════════════════════════════════════════════════════

const StreamingStatusBar = ({ statusSteps, isActive }) => {
  if (!statusSteps || statusSteps.length === 0) return null;

  return (
    <div className="cortex-streaming-status">
      <div className="cortex-streaming-status__steps">
        {statusSteps.map((step, idx) => {
          const isCurrent = idx === statusSteps.length - 1 && isActive;
          return (
            <div
              key={idx}
              className={`cortex-streaming-status__step ${
                isCurrent ? 'cortex-streaming-status__step--active' : 'cortex-streaming-status__step--done'
              }`}
            >
              {isCurrent ? (
                <span className="cortex-streaming-dot" />
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" className="cortex-streaming-check">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              <span>{step}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StreamingThinking = ({ thinkingSteps, isActive }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current && isActive) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thinkingSteps, isActive]);

  if (!thinkingSteps || thinkingSteps.length === 0) return null;

  return (
    <div className={`cortex-streaming-thinking ${isActive ? 'cortex-streaming-thinking--active' : ''}`}>
      <div className="cortex-streaming-thinking__header">
        <span className="cortex-streaming-thinking__icon">🧠</span>
        <span className="cortex-streaming-thinking__title">
          {isActive ? 'Thinking...' : 'Thinking & Reasoning'}
        </span>
        {isActive && <span className="cortex-streaming-pulse" />}
      </div>
      <div className="cortex-streaming-thinking__body" ref={scrollRef}>
        {thinkingSteps.map((step, idx) => (
          <div key={idx} className="cortex-streaming-thinking__step">
            <span className="cortex-streaming-thinking__text">{step}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TypingCursor = () => (
  <span className="cortex-typing-cursor" aria-hidden="true">▊</span>
);

// ════════════════════════════════════════════════════════════════
// DATA TABLE (shared — used by both Cortex and legacy paths)
// ════════════════════════════════════════════════════════════════

const DataTable = ({ data, columns }) => {
  if (!data || !data.length || !columns || !columns.length) return null;

  return (
    <div className="data-table-container">
      <div className="data-table-header">
        <span className="table-icon">📊</span>
        <span>Data Results ({data.length} rows)</span>
      </div>
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>{columns.map((col, idx) => <th key={idx}>{col}</th>)}</tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col, colIdx) => {
                  const cell = row[col];
                  const display =
                    cell === null || cell === undefined ? ''
                    : typeof cell === 'number'
                      ? cell.toLocaleString('en-IN', { maximumFractionDigits: 2 })
                      : String(cell);
                  return <td key={colIdx}>{display}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// RESULT VIEW — Chart/Table toggle (matches Snowflake Cortex UI)
// ════════════════════════════════════════════════════════════════

const ResultView = ({ chartSpec, data, columns, defaultView = 'chart' }) => {
  const hasChart = !!chartSpec;
  const hasTable = Array.isArray(data) && data.length > 0
                   && Array.isArray(columns) && columns.length > 0;

  const [view, setView] = useState(
    hasChart ? defaultView : (hasTable ? 'table' : 'chart')
  );

  if (!hasChart && !hasTable) return null;

  const bothAvailable = hasChart && hasTable;

  return (
    <div className="result-view">
      {bothAvailable && (
        <div className="result-view__toolbar">
          <div className="result-view__toggle" role="tablist" aria-label="View mode">
            <button
              role="tab"
              aria-selected={view === 'chart'}
              className={`result-view__toggle-btn ${view === 'chart' ? 'result-view__toggle-btn--active' : ''}`}
              onClick={() => setView('chart')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="20" x2="12" y2="10" />
                <line x1="18" y1="20" x2="18" y2="4" />
                <line x1="6" y1="20" x2="6" y2="16" />
              </svg>
              Chart
            </button>
            <button
              role="tab"
              aria-selected={view === 'table'}
              className={`result-view__toggle-btn ${view === 'table' ? 'result-view__toggle-btn--active' : ''}`}
              onClick={() => setView('table')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
              Table
            </button>
          </div>
        </div>
      )}

      <div className="result-view__body">
        {/* Single source of truth: `view` is initialized based on what's
            available (see useState above). When only chart exists, view='chart'.
            When only table exists, view='table'. When both, view='chart' by
            default and the toggle switches it. The two conditions below are
            mutually exclusive given `view` can only hold one value. */}
        {view === 'chart' && hasChart && <VegaLiteChart spec={chartSpec} />}
        {view === 'table' && hasTable && <DataTable data={data} columns={columns} />}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// CORTEX RESPONSE RENDERER (supports both streaming and final)
// ════════════════════════════════════════════════════════════════

const CortexResponse = ({ message }) => {
  const cortexData = message.cortexData || {};
  const thinkingSteps = cortexData.thinking_steps || [];
  const charts = cortexData.charts || [];
  const statusSteps = cortexData.status_steps || [];
  const isStreaming = message.isStreaming === true;

  // ── NEW: tabular data (populated from response.tool_result) ──
  const tableData = message.data || [];
  const tableColumns = message.columns || [];
  const hasTable = tableData.length > 0 && tableColumns.length > 0;

  const hasThinking = thinkingSteps.length > 0;
  const hasCharts = charts.length > 0;
  const hasStatus = statusSteps.length > 0;
  const hasExtras = hasThinking || hasStatus;
  const hasContent = message.content && message.content.trim().length > 0;

  // ── Dedupe charts (defensive) ──
  // Even though the backend dedupes, we do it here too because:
  //  1) Stream and 'done' both deliver charts — if the hook merges
  //     from both sources, duplicates can reappear.
  //  2) Safety against edge cases in agent re-emissions.
  // Dedup key: (title, mark, first row signature, value count) — same
  // fingerprint logic as the backend.
  const dedupedCharts = [];
  const seenFingerprints = new Set();
  for (const spec of charts) {
    if (!spec || typeof spec !== 'object') continue;
    let title = spec.title || '';
    if (typeof title === 'object' && title !== null) title = title.text || '';
    let mark = spec.mark || '';
    if (typeof mark === 'object' && mark !== null) mark = mark.type || '';
    const values = (spec.data && spec.data.values) || [];
    let firstRowSig = '';
    if (values.length && typeof values[0] === 'object') {
      firstRowSig = Object.keys(values[0])
        .sort()
        .map(k => `${k}=${values[0][k]}`)
        .join('|')
        .slice(0, 200);
    }
    const fp = `${title}||${mark}||${values.length}||${firstRowSig}`;
    if (!seenFingerprints.has(fp)) {
      seenFingerprints.add(fp);
      dedupedCharts.push(spec);
    }
  }

  // Build the list of result panels.
  //
  // Layout rules (match Snowflake's Cortex UI):
  //  - If we have BOTH chart(s) and table data → pair them into ONE panel
  //    with the Chart/Table toggle. Use the LAST chart (final version
  //    after any revisions during streaming).
  //  - If we have MULTIPLE distinct charts and a table, render the
  //    additional charts as separate panels without tables (so the
  //    table doesn't appear multiple times).
  //  - If only chart(s) and no table → each chart is its own panel
  //    (no toggle visible — <ResultView> handles that).
  //  - If only table and no chart → single table panel.
  const resultPanels = [];
  if (dedupedCharts.length > 0) {
    // Additional charts (if any) render first, standalone.
    for (let i = 0; i < dedupedCharts.length - 1; i++) {
      resultPanels.push({ chartSpec: dedupedCharts[i], data: null, columns: null });
    }
    // Primary (last) chart is paired with the table if available.
    const primaryChart = dedupedCharts[dedupedCharts.length - 1];
    resultPanels.push({
      chartSpec: primaryChart,
      data: hasTable ? tableData : null,
      columns: hasTable ? tableColumns : null,
    });
  } else if (hasTable) {
    resultPanels.push({ chartSpec: null, data: tableData, columns: tableColumns });
  }

  // ── STREAMING STATE ────────────────────────────────────────
  if (isStreaming) {
    return (
      <div className="cortex-response cortex-response--streaming">
        {/* Live status steps */}
        {hasStatus && (
          <StreamingStatusBar statusSteps={statusSteps} isActive={true} />
        )}

        {/* Live thinking panel */}
        {hasThinking && (
          <StreamingThinking thinkingSteps={thinkingSteps} isActive={!hasContent} />
        )}

        {/* Answer text (builds up progressively) */}
        {hasContent && (
          <div className="cortex-answer cortex-answer--streaming">
            <MarkdownContent content={message.content} />
            <TypingCursor />
          </div>
        )}

        {/* Charts + tables (can arrive mid-stream) */}
        {resultPanels.length > 0 && (
          <div className="cortex-results">
            {resultPanels.map((panel, idx) => (
              <ResultView
                key={idx}
                chartSpec={panel.chartSpec}
                data={panel.data}
                columns={panel.columns}
              />
            ))}
          </div>
        )}

        {/* Loading indicator when no content yet */}
        {!hasContent && !hasThinking && !hasStatus && (
          <div className="cortex-streaming-waiting">
            <div className="cortex-streaming-dots">
              <span /><span /><span />
            </div>
            <span>Connecting to agent...</span>
          </div>
        )}
      </div>
    );
  }

  // ── FINAL STATE ────────────────────────────────────────────
  return (
    <div className="cortex-response">
      {hasExtras && (
        <div className="cortex-extras">
          {hasThinking && (
            <ExpandableSection icon="🧠" title="Thinking & Reasoning"
              badge={`${thinkingSteps.length} step${thinkingSteps.length > 1 ? 's' : ''}`}>
              <div className="cortex-thinking-list">
                {thinkingSteps.map((step, idx) => (
                  <div key={idx} className="cortex-thinking-step">
                    <div className="cortex-thinking-step__number">{idx + 1}</div>
                    <div className="cortex-thinking-step__text">
                      <MarkdownContent content={step} />
                    </div>
                  </div>
                ))}
              </div>
            </ExpandableSection>
          )}

          {hasStatus && (
            <ExpandableSection icon="⚡" title="Processing Steps" badge={`${statusSteps.length}`}>
              <div className="cortex-status-list">
                {statusSteps.map((step, idx) => (
                  <div key={idx} className="cortex-status-step">
                    <div className="cortex-status-dot" />
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </ExpandableSection>
          )}
        </div>
      )}

      <div className="cortex-answer">
        <MarkdownContent content={message.content} />
      </div>

      {resultPanels.length > 0 && (
        <div className="cortex-results">
          {resultPanels.map((panel, idx) => (
            <ResultView
              key={idx}
              chartSpec={panel.chartSpec}
              data={panel.data}
              columns={panel.columns}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// COPY BUTTON — sits next to ShareButton in the action row
// ════════════════════════════════════════════════════════════════

const CopyButton = ({ message }) => {
  const [copied, setCopied] = useState(false);
  if (!message) return null;

  const handleCopy = async (e) => {
    e.stopPropagation();

    // Build a plaintext payload: answer text + table (if any)
    let payload = message.content || '';

    if (message.data && message.data.length > 0 && message.columns && message.columns.length > 0) {
      const header = message.columns.join('\t');
      const rows = message.data.map(row =>
        message.columns.map(col => {
          const v = row[col];
          return v === null || v === undefined ? '' : String(v);
        }).join('\t')
      );
      payload += '\n\n' + header + '\n' + rows.join('\n');
    }

    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Fallback for older browsers / non-secure (http) contexts
      const ta = document.createElement('textarea');
      ta.value = payload;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
  };

  return (
    <button
      className={`share-btn copy-btn ${copied ? 'copy-btn--copied' : ''}`}
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy response'}
      aria-label="Copy this response to clipboard"
    >
      {copied ? (
        <svg className="share-btn__icon" viewBox="0 0 20 20" fill="none"
             xmlns="http://www.w3.org/2000/svg">
          <path d="M4 10.5L8 14.5L16 6.5" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg className="share-btn__icon" viewBox="0 0 20 20" fill="none"
             xmlns="http://www.w3.org/2000/svg">
          <rect x="6" y="6" width="10" height="12" rx="1.5"
                stroke="currentColor" strokeWidth="1.5" />
          <path d="M4 14V4.5C4 3.67 4.67 3 5.5 3H13"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      )}
    </button>
  );
};

// ════════════════════════════════════════════════════════════════
// MESSAGE COMPONENT
// ════════════════════════════════════════════════════════════════

const Message = ({ message, isUser }) => {
  const isCortex = !isUser && message.queryEngine === 'cortex';

  return (
    <div className={`message ${isUser ? 'user-message' : 'assistant-message'}`}>
      <div className="message-avatar">{isUser ? '👤' : '🤖'}</div>
      <div className="message-content">
        {isCortex ? (
          <CortexResponse message={message} />
        ) : (
          <>
            <div className="message-text">
              <MarkdownContent content={message.content} />
            </div>
            {message.chartConfig && <ChartVisualization chartConfig={message.chartConfig} />}
            {/* Legacy (non-cortex) path — standalone data table */}
            {message.data && message.data.length > 0 && (
              <DataTable data={message.data} columns={message.columns} />
            )}
          </>
        )}

        {/* ── Action row: Copy + Share ── */}
        {!isUser && (
          <div className="message-actions">
            <CopyButton message={message} />
            {message.onShare && (
              <ShareButton message={message} onShare={message.onShare} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Message;
